import { useEffect, useState } from "react";
import { Button } from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";

// Disable / Re-enable / Archive — one configurable reason-note modal backing
// all four admin status actions (disable / re-enable / archive / unarchive).
// Mirrors DeclineProposalModal: a controlled textarea, min-length validation,
// an "audit-logged" warning, and an explicit (optionally destructive) confirm.
// Disable + re-enable require a reason (≥ minLength); archive + unarchive
// accept an empty reason.

interface AccountStatusModalProps {
  open: boolean;
  title: string;
  description?: string;
  label: string;
  placeholder?: string;
  reasonRequired: boolean;
  minLength?: number;
  confirmLabel: string;
  destructive?: boolean;
  pending: boolean;
  onConfirm: (reason: string) => void;
  onClose: () => void;
}

export function AccountStatusModal({
  open,
  title,
  description,
  label,
  placeholder,
  reasonRequired,
  minLength = 10,
  confirmLabel,
  destructive = false,
  pending,
  onConfirm,
  onClose,
}: AccountStatusModalProps) {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!open) setReason("");
  }, [open]);

  const trimmed = reason.trim();
  const valid = reasonRequired ? trimmed.length >= minLength : true;
  const showHint = reasonRequired && reason.length > 0 && !valid;

  function submit() {
    if (!valid || pending) return;
    onConfirm(trimmed);
  }

  return (
    <Modal
      open={open}
      onClose={pending ? () => {} : onClose}
      dismissOnBackdropClick={false}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button
            variant={destructive ? "destructive" : "primary"}
            onClick={submit}
            disabled={!valid || pending}
            loading={pending}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="grid gap-space-3">
        <p className="text-small text-gray-mid">This action is audit-logged.</p>
        <label
          htmlFor="account-status-reason"
          className="block text-small font-display uppercase tracking-label text-gold-shadow"
        >
          {label}
          {reasonRequired ? "" : " (optional)"}
        </label>
        <textarea
          id="account-status-reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border border-border-light rounded-md p-space-2 text-small focus:outline-none focus-visible:shadow-goldRing"
          placeholder={placeholder}
          rows={4}
          disabled={pending}
          aria-invalid={showHint || undefined}
        />
        {showHint && (
          <p className="text-caption text-status-error" role="alert">
            Reason must be at least {minLength} characters.
          </p>
        )}
      </div>
    </Modal>
  );
}
